import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Sprout, ShoppingBag, ShieldCheck, CreditCard, Bell,
  ChevronRight, Menu, X, ArrowRight, CheckCircle2,
  BarChart3, PackageSearch, ClipboardList, Truck, Star,
  Leaf, TrendingUp, Users, ShoppingCart, Award, Zap
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

function useCounter(target: number, started: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    let current = 0;
    const duration = 1800;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      current += step;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(current);
    }, 16);
    return () => clearInterval(timer);
  }, [target, started]);
  return count;
}

function StatItem({ value, label, suffix = "" }: { value: number; label: string; suffix?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const count = useCounter(value, inView);
  return (
    <div ref={ref} className="text-center">
      <div className="text-5xl font-bold mb-2" style={{ color: "var(--primary)", fontFamily: "var(--font-sans)" }}>
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--muted-foreground)" }}>{label}</div>
    </div>
  );
}

export default function Index() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (typeof window !== "undefined") setScrolled(window.scrollY > 20);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", onScroll);
      return () => window.removeEventListener("scroll", onScroll);
    }
  }, []);

  const scrollTo = (id: string) => {
    if (typeof document !== "undefined") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
    setMenuOpen(false);
  };

  const navLinks = [
    { label: "Features", id: "features" },
    { label: "How It Works", id: "how-it-works" },
    { label: "For Farmers", id: "for-farmers" },
    { label: "For Buyers", id: "for-buyers" },
    { label: "About", id: "stats" },
  ];

  const modules = [
    {
      icon: <Sprout className="w-6 h-6" />,
      title: "Farmer Portal",
      desc: "Register, manage product listings, update inventory and monitor incoming orders all in one place.",
      iconBg: "bg-green-500/15 text-green-700",
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "Buyer Marketplace",
      desc: "Browse fresh agricultural products, place orders seamlessly, and review complete purchase history.",
      iconBg: "bg-blue-500/15 text-blue-700",
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: "Admin Dashboard",
      desc: "Manage users, moderate product listings, generate reports and configure the platform.",
      iconBg: "bg-purple-500/15 text-purple-700",
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Secure Transactions",
      desc: "Payment processing, gateway integration, transaction recording and real-time order status updates.",
      iconBg: "bg-amber-500/15 text-amber-700",
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Smart Notifications",
      desc: "Instant order alerts, payment confirmations and system announcements for all platform users.",
      iconBg: "bg-rose-500/15 text-rose-700",
    },
  ];

  const steps = [
    { step: "01", icon: <Users className="w-7 h-7" />, title: "Register & Verify", desc: "Create your account as a Farmer or Buyer. Complete profile setup with contact and location details." },
    { step: "02", icon: <PackageSearch className="w-7 h-7" />, title: "List or Browse", desc: "Farmers publish product listings with prices and stock. Buyers search and filter available products." },
    { step: "03", icon: <ShoppingCart className="w-7 h-7" />, title: "Order & Pay", desc: "Buyers place orders and complete secure payment. The system notifies the farmer automatically." },
    { step: "04", icon: <Truck className="w-7 h-7" />, title: "Deliver & Confirm", desc: "Track order status in real-time. Receive confirmation and maintain complete purchase history." },
  ];

  const farmerFeatures = [
    "Add and manage agricultural product listings",
    "Set prices, update stock quantity in real-time",
    "Receive instant order notification alerts",
    "View sales performance and earnings report",
    "Manage farm profile and contact details",
  ];

  const buyerFeatures = [
    "Search and filter fresh agricultural products",
    "Browse detailed product information and prices",
    "Place orders with secure payment gateway",
    "Track order status from placement to delivery",
    "Access complete purchase history anytime",
  ];

  return (
    <div className="min-h-screen" style={{ background: "var(--background)", fontFamily: "var(--font-sans)" }}>

      {/* ─── NAVBAR ─── */}
      {/* @section: navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? "oklch(0.99 0.005 80 / 0.95)" : "transparent",
          borderBottom: scrolled ? "1px solid oklch(0.90 0.02 150 / 0.4)" : "none",
          boxShadow: scrolled ? "0 4px 24px -8px oklch(0.48 0.14 150 / 0.12)" : "none",
        }}>
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => scrollTo("hero")}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.62 0.16 145))" }}>
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold" style={{ color: "var(--foreground)" }}>
              Agri<span style={{ color: "var(--primary)" }}>Link</span>
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)}
                className="text-sm font-medium transition-colors cursor-pointer hover:opacity-70"
                style={{ color: scrolled ? "var(--muted-foreground)" : "var(--muted-foreground)", background: "none", border: "none" }}>
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button className="text-sm font-semibold px-4 py-2 rounded-lg transition-all hover:opacity-70"
              style={{ color: "var(--primary)", background: "none", border: "none", cursor: "pointer" }}>
              Login
            </button>
            <button className="text-sm font-semibold px-5 py-2.5 rounded-xl text-white transition-all hover:opacity-90"
              style={{
                background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.55 0.16 145))",
                boxShadow: "0 4px 14px -3px oklch(0.48 0.14 150 / 0.45)",
                border: "none", cursor: "pointer"
              }}>
              Get Started
            </button>
          </div>

          <button className="md:hidden p-2 rounded-lg" onClick={() => setMenuOpen(!menuOpen)}
            style={{ background: "var(--muted)", border: "none", cursor: "pointer" }}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden px-6 pb-4 flex flex-col gap-3"
            style={{ background: "var(--card)", borderTop: "1px solid var(--border)" }}>
            {navLinks.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)}
                className="text-sm font-medium py-2 text-left hover:opacity-70"
                style={{ color: "var(--foreground)", background: "none", border: "none", cursor: "pointer" }}>
                {l.label}
              </button>
            ))}
            <button className="mt-2 text-sm font-semibold py-3 rounded-xl text-white"
              style={{ background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.55 0.16 145))", border: "none", cursor: "pointer" }}>
              Get Started
            </button>
          </div>
        )}
      </header>

      {/* ─── HERO ─── */}
      {/* @section: hero */}
      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20"
            style={{ background: "radial-gradient(circle, oklch(0.62 0.16 145), transparent 70%)" }} />
          <div className="absolute bottom-0 -left-24 w-80 h-80 rounded-full opacity-15"
            style={{ background: "radial-gradient(circle, oklch(0.48 0.14 150), transparent 70%)" }} />
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="oklch(0.48 0.14 150 / 0.06)" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-8"
              style={{ background: "oklch(0.48 0.14 150 / 0.1)", color: "var(--primary)", border: "1px solid oklch(0.48 0.14 150 / 0.2)" }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--primary)" }} />
              KK24803 Software Design · Group 1 · Section 1 · UMS
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="text-5xl md:text-7xl font-extrabold leading-tight mb-6"
              style={{ color: "var(--foreground)", letterSpacing: "-0.02em" }}>
              Connecting{" "}
              <span style={{
                background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.62 0.16 145))",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
              }}>Farmers</span>{" "}
              to{" "}
              <span style={{
                background: "linear-gradient(135deg, oklch(0.42 0.08 35), oklch(0.55 0.10 40))",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
              }}>Buyers</span>,<br />Digitally.
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto"
              style={{ color: "var(--muted-foreground)" }}>
              AgriLink is a digital supply chain platform that empowers farmers to list products
              and enables buyers to purchase directly — with secure payments, real-time notifications,
              and seamless order management.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button onClick={() => scrollTo("for-farmers")}
                className="flex items-center gap-2 px-8 py-4 rounded-2xl text-white font-semibold text-base transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.55 0.16 145))",
                  boxShadow: "0 8px 24px -6px oklch(0.48 0.14 150 / 0.5)",
                  border: "none", cursor: "pointer"
                }}>
                <Sprout className="w-5 h-5" /> I'm a Farmer
              </button>
              <button onClick={() => scrollTo("for-buyers")}
                className="flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-base transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: "var(--card)", color: "var(--foreground)",
                  border: "1px solid var(--border)",
                  boxShadow: "0 4px 16px -4px oklch(0.42 0.08 35 / 0.15)",
                  cursor: "pointer"
                }}>
                <ShoppingBag className="w-5 h-5" /> I'm a Buyer
              </button>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-16 grid grid-cols-3 gap-4 max-w-xl mx-auto">
              {[
                { icon: <Leaf className="w-5 h-5" />, label: "Farmers", value: "500+" },
                { icon: <ShoppingCart className="w-5 h-5" />, label: "Products", value: "2,000+" },
                { icon: <CheckCircle2 className="w-5 h-5" />, label: "Orders", value: "10,000+" },
              ].map((m) => (
                <div key={m.label} className="rounded-2xl p-4 text-center"
                  style={{ background: "var(--card)", border: "1px solid var(--border)", boxShadow: "0 4px 16px -4px oklch(0.48 0.14 150 / 0.08)" }}>
                  <div className="flex justify-center mb-2" style={{ color: "var(--primary)" }}>{m.icon}</div>
                  <div className="text-xl font-bold" style={{ color: "var(--foreground)" }}>{m.value}</div>
                  <div className="text-xs font-medium" style={{ color: "var(--muted-foreground)" }}>{m.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronRight className="w-6 h-6 rotate-90" style={{ color: "var(--muted-foreground)" }} />
        </motion.div>
      </section>

      {/* ─── FEATURES ─── */}
      {/* @section: features */}
      <section id="features" className="py-24 px-6">
        <div className="container mx-auto">
          <motion.div className="text-center mb-16"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
              style={{ background: "oklch(0.48 0.14 150 / 0.1)", color: "var(--primary)" }}>
              Platform Modules
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4" style={{ color: "var(--foreground)" }}>
              Everything in One Platform
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: "var(--muted-foreground)" }}>
              Five integrated modules covering the entire agricultural supply chain — from listing to delivery.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            {modules.map((m, i) => (
              <motion.div key={i} variants={fadeUp}
                className={`rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1 ${i === 4 ? "md:col-start-2 lg:col-start-auto" : ""}`}
                style={{ background: "var(--card)", borderColor: "var(--border)", boxShadow: "0 4px 20px -6px oklch(0.48 0.14 150 / 0.08)" }}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${m.iconBg}`}>{m.icon}</div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "var(--foreground)" }}>{m.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>{m.desc}</p>
                <div className="mt-4 flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--primary)" }}>
                  Learn more <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      {/* @section: how-it-works */}
      <section id="how-it-works" className="py-24 px-6"
        style={{ background: "oklch(0.48 0.14 150 / 0.04)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="container mx-auto">
          <motion.div className="text-center mb-16"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
              style={{ background: "oklch(0.48 0.14 150 / 0.1)", color: "var(--primary)" }}>
              How It Works
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4" style={{ color: "var(--foreground)" }}>
              Simple. Transparent. Efficient.
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: "var(--muted-foreground)" }}>
              Four steps from registration to completed delivery.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <div className="hidden lg:block absolute top-14 left-[12.5%] right-[12.5%] h-0.5 z-0"
              style={{ background: "linear-gradient(90deg, oklch(0.48 0.14 150 / 0.3), oklch(0.62 0.16 145 / 0.3))" }} />
            {steps.map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="relative z-10 text-center">
                <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center text-white"
                  style={{
                    background: `linear-gradient(135deg, oklch(${0.44 + i * 0.04} 0.14 150), oklch(${0.56 + i * 0.03} 0.16 145))`,
                    boxShadow: "0 8px 20px -6px oklch(0.48 0.14 150 / 0.4)"
                  }}>
                  {s.icon}
                </div>
                <div className="text-xs font-bold tracking-widest mb-2" style={{ color: "var(--primary)" }}>STEP {s.step}</div>
                <h3 className="text-base font-bold mb-2" style={{ color: "var(--foreground)" }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── FOR FARMERS ─── */}
      {/* @section: for-farmers */}
      <section id="for-farmers" className="py-24 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="rounded-3xl p-8 relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.38 0.12 150))",
                  boxShadow: "0 24px 48px -12px oklch(0.48 0.14 150 / 0.4)"
                }}>
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10 pointer-events-none"
                  style={{ background: "radial-gradient(circle, white, transparent 70%)", transform: "translate(30%, -30%)" }} />
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                    <Sprout className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">Farmer Dashboard</h3>
                  <p className="text-sm mb-8" style={{ color: "oklch(0.85 0.08 150)" }}>Your complete farm management hub</p>
                  <div className="space-y-3">
                    {[
                      { label: "Active Listings", value: "12", icon: <ClipboardList className="w-4 h-4" /> },
                      { label: "Pending Orders", value: "5", icon: <ShoppingCart className="w-4 h-4" /> },
                      { label: "Revenue (This Month)", value: "RM 4,250", icon: <BarChart3 className="w-4 h-4" /> },
                    ].map(item => (
                      <div key={item.label} className="flex items-center justify-between px-4 py-3 rounded-xl"
                        style={{ background: "rgba(255,255,255,0.12)" }}>
                        <div className="flex items-center gap-2 text-sm" style={{ color: "oklch(0.90 0.06 150)" }}>
                          {item.icon} {item.label}
                        </div>
                        <span className="text-white font-bold text-sm">{item.value}</span>
                      </div>
                    ))}
                    <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: "oklch(0.85 0.08 150)" }}>
                      <TrendingUp className="w-4 h-4" />
                      <span>↑ 18% increase from last month</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                style={{ background: "oklch(0.48 0.14 150 / 0.1)", color: "var(--primary)" }}>
                For Farmers
              </div>
              <h2 className="text-4xl font-extrabold mb-4" style={{ color: "var(--foreground)" }}>
                Sell Your Produce<br />
                <span style={{ color: "var(--primary)" }}>Directly &amp; Easily</span>
              </h2>
              <p className="text-base leading-relaxed mb-8" style={{ color: "var(--muted-foreground)" }}>
                AgriLink gives farmers a powerful digital storefront to reach buyers directly,
                eliminating middlemen and ensuring fair prices for fresh agricultural produce.
              </p>
              <ul className="space-y-3 mb-8">
                {farmerFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: "oklch(0.48 0.14 150 / 0.15)" }}>
                      <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
                    </div>
                    <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>{f}</span>
                  </li>
                ))}
              </ul>
              <button className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm transition-all hover:scale-[1.02]"
                style={{
                  background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.55 0.16 145))",
                  boxShadow: "0 6px 18px -4px oklch(0.48 0.14 150 / 0.4)",
                  border: "none", cursor: "pointer"
                }}>
                Register as Farmer <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── FOR BUYERS ─── */}
      {/* @section: for-buyers */}
      <section id="for-buyers" className="py-24 px-6"
        style={{ background: "oklch(0.42 0.08 35 / 0.04)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                style={{ background: "oklch(0.42 0.08 35 / 0.12)", color: "oklch(0.42 0.08 35)" }}>
                For Buyers
              </div>
              <h2 className="text-4xl font-extrabold mb-4" style={{ color: "var(--foreground)" }}>
                Fresh Produce,<br />
                <span style={{ color: "oklch(0.42 0.08 35)" }}>Direct from Farm</span>
              </h2>
              <p className="text-base leading-relaxed mb-8" style={{ color: "var(--muted-foreground)" }}>
                Browse a wide selection of fresh agricultural products, compare prices, and
                place secure orders directly from local farmers — all in one convenient platform.
              </p>
              <ul className="space-y-3 mb-8">
                {buyerFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: "oklch(0.42 0.08 35 / 0.12)" }}>
                      <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "oklch(0.42 0.08 35)" }} />
                    </div>
                    <span className="text-sm" style={{ color: "var(--muted-foreground)" }}>{f}</span>
                  </li>
                ))}
              </ul>
              <button className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02]"
                style={{
                  background: "linear-gradient(135deg, oklch(0.42 0.08 35), oklch(0.52 0.10 38))",
                  color: "white",
                  boxShadow: "0 6px 18px -4px oklch(0.42 0.08 35 / 0.4)",
                  border: "none", cursor: "pointer"
                }}>
                Start Shopping <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      {/* @section: stats */}
      <section id="stats" className="py-24 px-6">
        <div className="container mx-auto">
          <motion.div className="text-center mb-16"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
              style={{ background: "oklch(0.48 0.14 150 / 0.1)", color: "var(--primary)" }}>
              Platform Impact
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: "var(--foreground)" }}>
              Trusted by the Community
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              { value: 500, label: "Registered Farmers", suffix: "+" },
              { value: 2000, label: "Products Listed", suffix: "+" },
              { value: 10000, label: "Orders Completed", suffix: "+" },
              { value: 98, label: "Satisfaction Rate", suffix: "%" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl p-6"
                style={{ background: "var(--card)", border: "1px solid var(--border)", boxShadow: "0 4px 16px -4px oklch(0.48 0.14 150 / 0.08)" }}>
                <StatItem value={s.value} label={s.label} suffix={s.suffix} />
              </motion.div>
            ))}
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6"
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            {[
              { icon: <ShieldCheck className="w-8 h-8" />, title: "Secure Platform", desc: "All transactions processed through verified payment gateways with full encryption." },
              { icon: <Zap className="w-8 h-8" />, title: "Real-Time Updates", desc: "Instant notifications for orders, payments and inventory changes across all users." },
              { icon: <Award className="w-8 h-8" />, title: "Quality Assured", desc: "Admin-moderated product listings ensuring standards for all produce on the platform." },
            ].map((b, i) => (
              <motion.div key={i} variants={fadeUp}
                className="flex items-start gap-4 p-6 rounded-2xl"
                style={{ background: "var(--muted)" }}>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "oklch(0.48 0.14 150 / 0.12)", color: "var(--primary)" }}>
                  {b.icon}
                </div>
                <div>
                  <h4 className="font-bold mb-1" style={{ color: "var(--foreground)" }}>{b.title}</h4>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── CTA BANNER ─── */}
      {/* @section: cta-banner */}
      <section className="py-24 px-6">
        <div className="container mx-auto">
          <motion.div initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="rounded-3xl p-12 text-center relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.40 0.12 150) 50%, oklch(0.35 0.10 150))",
              boxShadow: "0 24px 48px -12px oklch(0.48 0.14 150 / 0.5)"
            }}>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-10"
                style={{ background: "radial-gradient(circle, white, transparent 70%)" }} />
              <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full opacity-10"
                style={{ background: "radial-gradient(circle, white, transparent 70%)" }} />
            </div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">Ready to Join AgriLink?</h2>
              <p className="text-lg mb-8 max-w-xl mx-auto" style={{ color: "oklch(0.90 0.07 150)" }}>
                Start connecting with farmers and buyers today. Register for free and
                be part of a smarter agricultural supply chain.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-8 py-4 rounded-2xl font-semibold text-base bg-white transition-all hover:scale-[1.02]"
                  style={{ color: "var(--primary)", border: "none", cursor: "pointer", boxShadow: "0 8px 20px -4px rgba(0,0,0,0.2)" }}>
                  Register as Farmer
                </button>
                <button className="px-8 py-4 rounded-2xl font-semibold text-base transition-all hover:scale-[1.02]"
                  style={{ background: "rgba(255,255,255,0.15)", color: "white", border: "1px solid rgba(255,255,255,0.3)", cursor: "pointer" }}>
                  Register as Buyer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      {/* @section: footer */}
      <footer className="py-12 px-6" style={{ borderTop: "1px solid var(--border)", background: "var(--card)" }}>
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, oklch(0.48 0.14 150), oklch(0.62 0.16 145))" }}>
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold" style={{ color: "var(--foreground)" }}>
                  Agri<span style={{ color: "var(--primary)" }}>Link</span>
                </span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
                A digital supply chain platform connecting farmers and buyers for a smarter agricultural future.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-sm mb-4 uppercase tracking-widest" style={{ color: "var(--foreground)" }}>Platform</h4>
              {["Farmer Portal", "Buyer Marketplace", "Admin Dashboard", "Transactions", "Notifications"].map(l => (
                <div key={l} className="text-sm mb-2.5 cursor-pointer hover:opacity-70 transition-opacity"
                  style={{ color: "var(--muted-foreground)" }}>{l}</div>
              ))}
            </div>

            <div>
              <h4 className="font-bold text-sm mb-4 uppercase tracking-widest" style={{ color: "var(--foreground)" }}>System Diagrams</h4>
              {["Context Diagram", "DFD Level 0", "ERD", "Class Diagram", "UI Design"].map(l => (
                <div key={l} className="text-sm mb-2.5 cursor-pointer hover:opacity-70 transition-opacity"
                  style={{ color: "var(--muted-foreground)" }}>{l}</div>
              ))}
            </div>

            <div>
              <h4 className="font-bold text-sm mb-4 uppercase tracking-widest" style={{ color: "var(--foreground)" }}>Course Info</h4>
              <div className="space-y-2 text-sm" style={{ color: "var(--muted-foreground)" }}>
                <div>KK24803 Software Design</div>
                <div>Semester 2, Session 2025/2026</div>
                <div>Section 1 · Group 1</div>
                <div>Dr. Norhayati binti Daut</div>
                <div className="pt-2 font-semibold" style={{ color: "var(--foreground)" }}>Universiti Malaysia Sabah</div>
              </div>
            </div>
          </div>

          <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4"
            style={{ borderTop: "1px solid var(--border)" }}>
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              © 2025 AgriLink · Group 1 · Faculty of Computing and Informatics · UMS
            </p>
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              Members: 1. ADREEN KELLY ABEL WANDY &nbsp;|&nbsp; 2. EIZHA ELISA BINTI JIPRI &nbsp;|&nbsp; 3. FREDERICA GEFFREY &nbsp;|&nbsp; 4. NURSAKINAH BINTI ARMAN ITTOK &nbsp;|&nbsp; 5. MARCYLYN JAFREN
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
